/**
 * ExcelProcessor.js provides support to parse HTML on the clipboard that originates from Microsoft Excel.
 * The module exports `isExcel` to determine if HTML originated from Excel, and `processExcel` for returning a
 * list of WYCIWYP tables. WYCIWYP's parseTable, alone, is not sufficient to parse the <table>s because
 * Excel uses:
 *
 *   a. <style> tags: parseTable does not have this context, so `processExcel` will inline relevant styles
 *      into the corresponding <td>
 *
 *   b. custom CSS: currently, this is used to correctly interpret the formatting of a cell; e.g. even though
 *      a cell may appear to be in scientific notation, the <td> may have the style:
 *      {mso-number-format: '\@';} which indicates that it should be processed as text. `processExcel` wraps
 *      any such text coercions via wrapping the numeric text in a text formula, e.g. `='...'`.
 *
 * As an example, see: testdata/fixtures/wyciwyp/excel-clipboard-number-format.html
 */

import excess from 'notebook/tables/excess';
import escapeRegExp from 'util/escapeRegExp';

import {ProcessClipboardOptions, ProcessNodeOptions} from './wyciwyp';
import {WYCIWYPPart} from './WyciwypConstants';

const MSO_NUMBER_FORMATS = {
  FIXED: 'Fixed',
  FIXED_DECIMALS_RE: /^"0(\.0+)?"$/,
  THOUSANDS_RE: /"#,##0(\.0+)?"/,
  STANDARD: 'Standard', // Same as: /"#,##0.00"/
  GENERAL: 'General',
  SCIENTIFIC: 'Scientific',
  SCIENTIFIC_DECIMALS_RE: /^"0\.0*E\+00"$/,
  TEXT: '"@"',
};

export const MSO_GEN_NUMERIC_FORMAT = /^(0|(-)?((0|[1-9]\d*)\.\d*[1-9]|[1-9]\d*))$/; // integer or decimal
export const MSO_GEN_SCIENTIFIC_FORMAT = /^(-)?([1-9]\.\d*[1-9]|[1-9])E[+-]\d+$/; // auto parsed scientific

const MSO_NUMBER_FORMAT_PROPERTY = 'mso-number-format';
const MSO_DECIMAL_SEPARATOR_PROPERTY = 'mso-displayed-decimal-separator';
const MSO_THOUSAND_SEPARATOR_PROPERTY = 'mso-displayed-thousand-separator';
const MSO_IGNORE_PROPERTY = 'mso-ignore';
const MSO_PROPERTIES = [
  MSO_NUMBER_FORMAT_PROPERTY,
  MSO_DECIMAL_SEPARATOR_PROPERTY,
  MSO_THOUSAND_SEPARATOR_PROPERTY,
];

/**
 * Determine if the HTML document corresponds to clipboard data from Excel; detection is based on the
 * presence of the Excel namespace in the HTML tag.
 *
 * @param {string} html
 * @return {boolean}
 */
export function isExcel(html: string): boolean {
  const htmlTagMatch = /<html\s+[\s\S]*?>/.exec(html);
  if (!htmlTagMatch) {
    return false;
  }
  return htmlTagMatch[0].includes('xmlns:x="urn:schemas-microsoft-com:office:excel"');
}

/**
 * Detect if Excel is rendering a cell as scientific. There are three mso-number-formats to consider:
 *   1. SCIENTIFIC_DECIMALS_RE: if the user has specified scientific with # digits after the decimal
 *   2. SCIENTIFIC: if the user has specified scientic without any particular decimals
 *   3. GENERAL: this tells Excel to auto-detect the format; if Excel detects scientific here, it will use
 *               a fairly standardized representation that MSO_GEN_SCIENTIFIC_FORMAT matches.
 *
 * @param {string} msoNumberFormat - the parsed mso-number-format from the cell.
 * @param {string?} text - contents of the cell
 * @return {boolean}
 */
function isMsoScientific(msoNumberFormat: string, text: string | null = null): boolean {
  // If the user specified a number of decimals, a custom format is used.
  if (MSO_NUMBER_FORMATS.SCIENTIFIC_DECIMALS_RE.test(msoNumberFormat)) {
    return true;
  }
  if (msoNumberFormat === MSO_NUMBER_FORMATS.SCIENTIFIC) {
    return true;
  }
  if (
    msoNumberFormat === MSO_NUMBER_FORMATS.GENERAL &&
    (text == null || MSO_GEN_SCIENTIFIC_FORMAT.test(text))
  ) {
    return true;
  }
  return false;
}

/**
 * Detect if Excel is rendering a cell as numeric. There are three mso-number-formats to consider:
 *   1. FIXED_DECIMALS_RE: if the user has specified numeric with # digits after the decimal
 *   2. FIXED: if the user has specified numeric without any particular decimals
 *   3. GENERAL: this tells Excel to auto-detect the format; if Excel detects numeric here, it will use
 *               a fairly standardized representation that MSO_GEN_NUMERIC_FORMAT matches.
 *
 * @param {string} msoNumberFormat - the parsed mso-number-format from the cell.
 * @param {string?} text - contents of the cell
 * @return {boolean}
 */
function isMsoNumeric(msoNumberFormat: string, text: string | null = null): boolean {
  // If the user specified a number of decimals, a custom format is used.
  if (MSO_NUMBER_FORMATS.FIXED_DECIMALS_RE.test(msoNumberFormat)) {
    return true;
  }
  if (msoNumberFormat === MSO_NUMBER_FORMATS.FIXED || msoNumberFormat === MSO_NUMBER_FORMATS.STANDARD) {
    return true;
  }
  if (MSO_NUMBER_FORMATS.THOUSANDS_RE.test(msoNumberFormat)) {
    return true;
  }
  if (msoNumberFormat === MSO_NUMBER_FORMATS.GENERAL && (text == null || MSO_GEN_NUMERIC_FORMAT.test(text))) {
    return true;
  }
  return false;
}

function isAnyNumericType(msoNumberFormat: string, text: string | null = null): boolean {
  return isMsoScientific(msoNumberFormat, text) || isMsoNumeric(msoNumberFormat, text);
}

/**
 * Determine if Excess's type detection will incorrectly identify numeric-looking text as non-text.
 *
 * Generally, Excess's parsing of text will match that of Excel's, except for cases where the user overrides
 * the default behavior, which can be accomplished by:
 *   a. explicitly specifying a particular number format for the cell (e.g. `Text`)
 *   b. prefixing a numeric value with `'` to have it render as text
 *   c. wrapping a numerc value with `="..."` to have it render as text.
 *
 * @param {string} text - contents of the cell
 * @param {string} excessType - one of excess.util.VALUE_TYPES corresponding to `text`
 * @param {string} msoNumberFormat - the parsed mso-number-format from the cell
 * @return {boolean}
 */
function shouldCoerceToText(text: string, excessType: string, msoNumberFormat: string): boolean {
  // If we could not find a number format, we likely ran into trouble while parsing the CSS. Conservatively,
  // we parse the text as we normally would.
  if (msoNumberFormat == null) {
    return false;
  }
  // If Excel is reporting a known numeric type, then we should not coerce to text.
  if (isAnyNumericType(msoNumberFormat, text)) {
    return false;
  }
  // If we are already parse the text as a string, then we don't need any further coercion.
  if (excessType === excess.util.VALUE_TYPES.STRING) {
    return false;
  }
  return true;
}

/**
 * Because {mso-foo: bar;} is non-standard CSS, we can't rely on the CSSOM to extract the values (this
 * includes getting the CSS via cssRule.cssText). Instead, we parse it from the string, handling removing
 * the `\` escape character (used for `@`, `.`, `#`, `,`)
 *
 * @param  {string} rawCSSText - e.g. 'p.xyx { mso-number-format: General}'
 * @param  {string} propertyName - e.g. 'mso-number-format'
 * @return {string} - e.g. 'General'
 */
function getMsoPropertyValue(rawCSSText: string, propertyName: string): string | null {
  const regExp = new RegExp(`${propertyName}\\s*:\\s*(.+?)\\s*;`);
  const match = regExp.exec(rawCSSText);
  if (!match) {
    return null;
  }
  return match[1].replace(/\\/g, '');
}

interface CustomRule {
  cssRule: CSSStyleRule;
  msoStyles: {[propertyName: string]: string};
}

// Exposed for stubbing in tests
export const _DOMAPI = {
  getCSSRules(style: LinkStyle): CSSStyleSheet | null {
    return style.sheet;
  },
};

/**
 * Excel uses CSS with non-standard properties / values to store formatting data about cells, e.g.
 * `{ mso-number-format: Scientific; text-align: general}`; Because they are non-standard, we do not get
 * consistent behavior between jsdom / browsers' CSSOM implementation. Therefore, for every CSS rule, we
 * construct a JavaScript object that captures the standard styling as well as the custom ones:
 *
 * @typedef {Object} CustomRule
 * @property {Object} msoStyles - an object mapping custom mso styles to their parsed values, e.g.
 *                                {"mso-number-format": "@"}
 * @property {CSSStyleRule} cssRule - notably contains `selectorText` for matching a node, and `style` for
 *                                    the associated CSS.
 *
 * @param  {Document} document_ - document containing <style> nodes to transform
 * @return {CustomRule[]} - as defined above, ordered by specificity (most to least)
 */
function buildCustomRules(document_: Document): Array<CustomRule> {
  const customRules: Array<CustomRule> = [];
  Array.from(document_.getElementsByTagName('style')).forEach((style) => {
    // NB: Excel currently uses one non-standard CSS property value that we parse, {text-align: general};
    // this seems to have the meaning of use whatever alignment it would detect for text vs. number, which
    // our renderer already does; therefore, we can ignore it safely (as parseTable already does).
    // Note that if there were other non-standard CSS properties that we'd like to map to valid ones, we
    // could construct a new style.outerHTML and then use DOMParser to create a <style> javascript node.
    Array.from(_DOMAPI.getCSSRules(style)?.cssRules ?? []).forEach((cssRule: CSSStyleRule) => {
      const customRule: CustomRule = {
        cssRule,
        msoStyles: {},
      };
      // In Firefox, CSSPageRule's do not appear to have selectorText defined, so we skip it.
      if (cssRule.selectorText == null) {
        return;
      }
      // Excel wraps the style block in an HTML comment, e.g. <!--actual styles-->, which causes the first
      // selector to begin with <!--... at least with JSDOM >_>
      if (cssRule.selectorText.startsWith('<!--')) {
        cssRule.selectorText = cssRule.selectorText.slice(4);
      }
      // Browser (unlike JSDOM) implementation of cssRule.cssText skips over non-standard properties, so
      // we must parse out the cssText from the <style>'s innerHTML.
      const rawCSSTextRegex = new RegExp(`${cssRule.selectorText.replace('.', '\\.')}\\s*\\{[^}]*\\}`);
      const rawCSSTextMatch = rawCSSTextRegex.exec(style.innerHTML);
      if (rawCSSTextMatch) {
        const rawCSSText = rawCSSTextMatch[0];
        MSO_PROPERTIES.forEach((propertyName) => {
          const propertyValue = getMsoPropertyValue(rawCSSText, propertyName);
          if (propertyValue != null) {
            customRule.msoStyles[propertyName] = propertyValue;
          }
        });
      }
      customRules.unshift(customRule);
    });
  });
  return customRules;
}

/**
 * Apply customRules in order of specificity to td, returning a map from of custom CSS property name to value
 *
 * @param {HTMLTableDataCellElement} td
 * @param {CustomRule[]} customRules
 * @return {Object}
 */
function applyCustomRules(
  td: HTMLTableDataCellElement,
  customRules: Array<CustomRule>
): {[propertyName: string]: string} {
  const tdMsoStyles: {[propertyName: string]: string} = {};
  // Merge the styles of all the matching rules into the <td>'s style attribute so that WYCIWYP's
  // parseTable can process them.
  let node: HTMLElement = td;
  while (node) {
    for (const customRule of customRules) {
      let matches;
      try {
        matches = node.matches(customRule.cssRule.selectorText);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        // may throw SyntaxError for poorly formatted selectorText
        matches = false;
      }
      if (matches) {
        // Rules are ordered from most specific to least, so only apply if a property is unassigned

        // Merge in standard CSS properties so that WYCIWYP can process them (e.g. bold, underline, etc.)
        Array.from(customRule.cssRule.style).forEach((cssProp) => {
          if (!td.style.getPropertyValue(cssProp)) {
            td.style.setProperty(cssProp, customRule.cssRule.style.getPropertyValue(cssProp));
          }
        });

        // Merge in non-standard CSS properties into `tdMsoStyles` to be used to construct the return value
        Object.entries(customRule.msoStyles).forEach(([propertyName, propertyValue]) => {
          // eslint-disable-next-line benchlint/no-in-operator
          if (!(propertyName in tdMsoStyles)) {
            tdMsoStyles[propertyName] = propertyValue;
          }
        });
      }
    }
    // We match the rules against all parent nodes as a style applied to a node is inherited by children.
    node = node.parentNode as HTMLElement;
  }
  return tdMsoStyles;
}

function hasNoTextAlign(td: HTMLTableDataCellElement): boolean {
  return ['', 'general'].includes(td.style.getPropertyValue('text-align'));
}

function _stripQuotesAroundSingleCharacter(value: string): string | null {
  const match = /"(.)"/.exec(value);
  if (!match) {
    return null;
  }
  return match[1];
}

/**
 * Given the text and the localization information in tdMsoStyles, see if we can delocalize it into
 * a number with `.` decimal separator and `,` thousand separator.
 */
function _delocalizeText(text: string, tdMsoStyles: {[propertyName: string]: string}): string {
  const thousandSep = _stripQuotesAroundSingleCharacter(tdMsoStyles[MSO_THOUSAND_SEPARATOR_PROPERTY]) || ',';
  const decimalSep = _stripQuotesAroundSingleCharacter(tdMsoStyles[MSO_DECIMAL_SEPARATOR_PROPERTY]) || '.';

  // Extract out the decimal part of the number if we are dealing with scientific.
  // Note that we only have to deal with the strict representation captured in MSO_GEN_SCIENTIFIC_FORMAT.
  const [decimal, ...rest] = text.split(/(?=E[+-]\d+$)/);
  // 1.23 --> ["1.23"], --> decimal="1.23", rest=[]
  // 1E+23 --> ["1", "E+23"] --> decimal = "1", rest=["E+23"]
  if (rest.length > 1) {
    return text;
  }
  const scientificSuffix = rest.join(''); // e.g. "1E+23" --> "E+23"

  // Split on localized decimal separator (later to be joined on canonical `.`)
  const [wholePart, fractionalPart, ...extra] = decimal.split(decimalSep);
  if (extra.length > 0) {
    // There should be at most one decimal separator
    return text;
  }

  // Strip out thousand separator if the thousand separator is found to be correctly spaced every 3 digits.
  const escapedThousandSep = escapeRegExp(thousandSep);
  const thousandSeparatedIntRegExp = new RegExp(`^-?\\d{1,3}(${escapedThousandSep}?\\d{3})*$`);
  if (!thousandSeparatedIntRegExp.test(wholePart)) {
    return text;
  }
  const thousandSepRegExp = new RegExp(escapedThousandSep, 'g');
  const cleanWholePart = wholePart.replace(thousandSepRegExp, '');

  // Finally join, the number back together again with the `.` decimal separator.
  if (fractionalPart != null) {
    return `${cleanWholePart}.${fractionalPart}${scientificSuffix}`;
  } else {
    return `${cleanWholePart}${scientificSuffix}`;
  }
}

/**
 * Given the text and styles of a cell, determine the delocalized text / excess type.
 *
 * Delocalization supports:
 *  - Stripping the thousand separator (e.g. 1,234,567 or 1.123.456 to 1123456 depending on the thousand sep)
 *  - Replacing the decimal spearator with `.` (e.g. 1,234 becomes 1.234 if the decimal sep is `.`)
 *
 * If the text is deemed to be non-numeric, then the text will be preserved with type STRING.
 * Otherwise, the delocalized text will be returned along with the numeric type Excess would parse out.
 *
 * @param {text} text
 * @param {Object} tdMsoStyles
 *
 * @returns {Object} of shape {text, type}.
 */
function getDelocalizedText(text: string, tdMsoStyles: {[propertyName: string]: string}): string {
  // If the mso-number-format does not correspond to a numeric type, don't delocalize: just return a STRING.
  if (!isAnyNumericType(tdMsoStyles[MSO_NUMBER_FORMAT_PROPERTY], null)) {
    return text;
  }
  const delocalizedText = _delocalizeText(text, tdMsoStyles);
  if (!isAnyNumericType(tdMsoStyles[MSO_NUMBER_FORMAT_PROPERTY], delocalizedText)) {
    return text;
  }
  // If the delocalizedText matches the regex for a numeric type, then the delocalization was a success!
  return delocalizedText;
}

/**
 * Parse Excel HTML into WYCIWYP parts
 * @param  {string} html - HTML from copying cells from Excel; expected for isExcel(html) === true.
 * @param  {Function} processNode - function that returns a list of WYCIWYP parts for a DOM node.
 * @param  {Object} options - configured options for processNode that may be extended.
 * @return {Array} - a list of WYCIWYP parts.
 */
export function processExcel(
  html: string,
  processNode: (el: HTMLElement, options: ProcessNodeOptions) => Array<WYCIWYPPart>,
  options: ProcessClipboardOptions & {indentation: number}
): Array<WYCIWYPPart> {
  const parser = new DOMParser();
  const document_ = parser.parseFromString(html, 'text/html');
  const body = document_.body;

  // Chrome / Safari do not support computing styles unless it is attached to the DOM, see:
  // https://crbug.com/558165#c13 . This would require attaching the style and the DOM nodes which is not
  // ideal (potential self-XSS / styles being applied globally); so we collect the rules and merge the
  // appropriate styles into the <td>'s style attribute. NOTE: We observe that Excel uses same-specificity
  // rules, so we can simplify the style computation by favoring the styles of the last matching rule.
  const customRules = buildCustomRules(document_);

  // Use WYCIWYP's DOM processor but inject special logic to handle Excel's TD nodes
  const extendedOptions = {
    ...options,
    // We are relying on our Excel locale parsing logic, so signal to the parent that it doesn't need to run any additional locale parsing
    skipLocaleParsing: true,

    sanitizeAndParseTD(
      td: HTMLTableDataCellElement,
      originalText: string
    ): {formula?: string; ignoreColspan?: boolean} {
      // Before sanitizing style selectors, detect if Excel has added an implicit colspan for text overflowing its cell into an
      // empty cell. Excel does this by setting the colspan attr and including the inline custom style `mso-ignore:colspan;` on the TD.
      let ignoreColspan = false;
      if (getMsoPropertyValue(td.getAttribute('style') ?? '', MSO_IGNORE_PROPERTY)?.includes(`colspan`)) {
        ignoreColspan = true;
      }

      const tdMsoStyles = applyCustomRules(td, customRules);

      // Excel uses the `align` attribute to expose the alignment that it derived based on cell type;
      // not what the user explicitly specified (which would show up in the CSS as `text-align`).
      // The `align` attribute empirically is never defined if the td is styled with `text-align`, and in
      // some edge cases does not agree with our derived type; in such cases, we treat it as though the user
      // explicitly set it.
      const align = td.getAttribute('align');
      td.removeAttribute('align');

      const delocalizedText = getDelocalizedText(originalText, tdMsoStyles);
      const msoNumberProperty = tdMsoStyles[MSO_NUMBER_FORMAT_PROPERTY];
      const {type} = excess.util.parseValue(delocalizedText);
      const willCoerceToText = shouldCoerceToText(delocalizedText, type, msoNumberProperty);
      const willBeText = type === excess.util.VALUE_TYPES.STRING || willCoerceToText;
      if (willBeText && align === 'right') {
        td.style.setProperty('text-align', 'right');
      }
      if (!willBeText && !align && hasNoTextAlign(td)) {
        td.style.setProperty('text-align', 'left');
      }

      if (willCoerceToText) {
        // If we detect that Excel is formatting numeric cell content as text, we treat it as text by forcing
        // it to be text.
        return {formula: `="${delocalizedText}"`, ignoreColspan};
      } else if (originalText !== delocalizedText) {
        // Localization affected what the text should be.
        return {formula: delocalizedText, ignoreColspan};
      } else {
        return {ignoreColspan}; // no formula formatting overrides
      }
    },
  };

  // Attach the body to a fragment created from document, so that body.ownerDocument.defaultView will
  // will resolve to window properly, allowing us to use $.css() in WYCIWYP's processNode.
  // See: https://github.com/jquery/jquery/issues/2086
  document.createDocumentFragment().appendChild(body);
  const results: Array<WYCIWYPPart> = [];
  Array.from(body.getElementsByTagName('table')).forEach((table) => {
    results.push(...processNode(table, extendedOptions));
  });
  return results;
}
